// Import from specific files to avoid circular dependency via @src/editor barrel
import { Timeline } from '@src/editor';
import { LayoutTemplateRegistry, TemplateIds } from '@src/editor/models/layout';
import { buildBindingsForTemplate } from '@src/editor/models/layout/templateUtils';
import { EditorChangeType } from '@src/editor/types';
import { getSpeakerColor } from '@src/services/SpeakerManagementService';
import { LogManager } from '@src/utility/Logger';
import { convertTimeToMMSS } from '@src/utility/timeFormat';

import {
  buildSceneFramingIntervals,
  getLongestIntervalDetections,
} from './buildSceneFramingIntervals';
import { selectClusterExcludingLabels } from './otherSpeakerCluster';
import { extractSpeakerIntervals } from './speakerIntervalUtils';
import { rankSpeakersForScene } from './speakerRanking';

import type {
  ActiveSpeakerFaceDetections,
  FaceDetection,
  FaceToSpeakerLabelMapping,
  SceneFramingInterval,
  SpeakerInterval,
} from './types';
import type { Command } from '@src/editor/commands/Command';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { VideoElement } from '@src/editor/models/elements/VideoElement';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import type { Scene } from '@src/editor/models/layout/Scene';
import type { IScene } from '@src/editor/models/layout/types';
import type { SceneService } from '@src/services/SceneService';

const logger = LogManager.getLogger('SmartFramingService');

// Re-export shared framing types so existing consumers continue to work.
export type {
  ActiveSpeakerFaceDetections,
  FaceDetection,
  FaceToSpeakerLabelMapping,
  SceneFramingConfig,
  SceneFramingInterval,
  SpeakerIdentifications,
  SpeakerInterval,
} from './types';

/* Minimal interface for smart framing based on face detection.
 * Used to decouple scene framing from the full SmartFramingService.
 */
export interface ISceneFramingService {
  /**
   * Auto-frame a single scene based on face detections.
   * Applies optimal crop to the scene's layout bindings.
   * @param sceneInfo Scene with its resolved timeline times
   * @param faceDetections All face detections (in timeline time)
   * @returns true if framing was applied, false otherwise
   */
  autoFrameSingleScene(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    targetAspectRatio?: number,
  ): boolean;
}

type FaceBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Crop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ClusterInfo = {
  boxes: FaceBox[];
  /** Source-detection indices (into the input to `clusterFaceBoxes`), aligned with `boxes`. */
  boxIndices: number[];
  avgX: number;
  avgY: number;
};

const CLUSTER_DISTANCE_THRESHOLD = 0.25;

export class SmartFramingService implements ISceneFramingService {
  private editor: IVideoEditor;
  private sceneService: SceneService;
  private layoutEngine: LayoutEngine;
  private timelineSpeakersCache: {
    transcript: ReadonlyArray<unknown>;
    signature: string;
    speakers: readonly string[];
  } | null = null;

  constructor(
    editor: IVideoEditor,
    sceneService: SceneService,
    layoutEngine: LayoutEngine,
  ) {
    this.editor = editor;
    this.sceneService = sceneService;
    this.layoutEngine = layoutEngine;
    logger.debug('SmartFramingService initialized');
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private boxesOverlap(box1: FaceBox, box2: FaceBox): boolean {
    return !(
      box1.x + box1.width < box2.x ||
      box2.x + box2.width < box1.x ||
      box1.y + box1.height < box2.y ||
      box2.y + box2.height < box1.y
    );
  }

  clusterFaceBoxes(detections: FaceDetection[]): ClusterInfo[] {
    const allBoxes = detections.map(d => ({
      x: d.box[0],
      y: d.box[1],
      width: d.box[2],
      height: d.box[3],
    }));
    const clusters: FaceBox[][] = [];
    const clusterIndices: number[][] = [];

    for (let i = 0; i < allBoxes.length; i++) {
      const box = allBoxes[i];
      let addedToCluster = false;

      for (let c = 0; c < clusters.length; c++) {
        if (
          clusters[c].some(clusterBox => this.boxesOverlap(box, clusterBox))
        ) {
          clusters[c].push(box);
          clusterIndices[c].push(i);
          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusters.push([box]);
        clusterIndices.push([i]);
      }
    }

    return clusters.map((cluster, c) => {
      const avgX =
        cluster.reduce((sum, box) => sum + box.x + box.width / 2, 0) /
        cluster.length;
      const avgY =
        cluster.reduce((sum, box) => sum + box.y + box.height / 2, 0) /
        cluster.length;
      return { boxes: cluster, boxIndices: clusterIndices[c], avgX, avgY };
    });
  }

  private getCropAspectRatioForLayout(
    layout: { layoutTemplateId?: string },
    targetAspectRatio?: number,
  ): number {
    const baseAspect = targetAspectRatio ?? 9 / 16;
    if (layout.layoutTemplateId === 'split_horizontal') {
      return baseAspect * 2;
    }
    return baseAspect;
  }

  private getCropZoomForLayout(layout: { layoutTemplateId?: string }): number {
    if (layout.layoutTemplateId === 'split_horizontal') {
      return 1.15;
    }
    return 1.0;
  }

  calculateCropBoxForCluster(
    cluster: ClusterInfo,
    aspectRatio: number,
    zoom: number,
  ): Crop {
    const centerX = cluster.avgX;
    let cropHeight = 1 / zoom;
    let cropWidth = (cropHeight * aspectRatio) / (16 / 9);

    return {
      x: Math.max(0, Math.min(1 - cropWidth, centerX - cropWidth / 2)),
      y: 0,
      width: cropWidth,
      height: cropHeight,
    };
  }

  getDetectionsForElement(
    detections: FaceDetection[],
    el: VideoElement,
  ): FaceDetection[] {
    const elMediaStart = el.getMediaStartTime();
    const elMediaEnd = el.getMediaEndTime();

    const elDetections = detections.reduce<FaceDetection[]>(
      (acc, detection) => {
        if (detection.time >= elMediaStart && detection.time <= elMediaEnd) {
          acc.push({
            ...detection,
            time: el.time + detection.time - elMediaStart,
          });
        }
        return acc;
      },
      [],
    );

    return elDetections;
  }

  /**
   * Extracts continuous speaker intervals from transcript elements.
   * Delegates to the shared pure utility in speakerIntervalUtils.
   */
  private extractSpeakerIntervals(
    transcriptElements: ReadonlyArray<{
      ts?: number | null;
      end_ts?: number | null;
      speaker_label?: string;
      type?: string;
    }>,
  ): SpeakerInterval[] {
    return extractSpeakerIntervals(transcriptElements);
  }

  /**
   * Remap speaker intervals from **media time** (original video source) to
   * **timeline time** using the overlapping elements, analogous to how
   * `getDetectionsForElement` remaps face detections.
   *
   * Each overlapping element defines a media→timeline mapping:
   *   `timeline_t = el.time + (media_t − elMediaStart)`
   *
   * A speaker interval that spans multiple elements is clipped per-element
   * and may produce separate remapped intervals.  Intervals that don't
   * overlap any element are dropped.
   */
  private remapSpeakerIntervalsToTimeline(
    speakerIntervals: readonly SpeakerInterval[],
    overlappingElements: readonly VideoTranscriptElement[],
  ): SpeakerInterval[] {
    const remapped: SpeakerInterval[] = [];

    for (const si of speakerIntervals) {
      for (const el of overlappingElements) {
        const elMediaStart = el.getMediaStartTime();
        const elMediaEnd = el.getMediaEndTime();

        // Skip if no overlap between speaker interval and element media range.
        if (si.endTime <= elMediaStart || si.startTime >= elMediaEnd) continue;

        // Clip speaker interval to element's media range.
        const clippedStart = Math.max(si.startTime, elMediaStart);
        const clippedEnd = Math.min(si.endTime, elMediaEnd);

        // Remap to timeline time.
        const offset = el.time - elMediaStart;
        remapped.push({
          speakerLabel: si.speakerLabel,
          startTime: clippedStart + offset,
          endTime: clippedEnd + offset,
        });
      }
    }

    // Sort by timeline start time (elements may be in any order).
    remapped.sort((a, b) => a.startTime - b.startTime);
    return remapped;
  }

  /**
   * Calculates face detections that belong to active speakers.
   * Uses speaker intervals from transcript to determine who is speaking at each moment,
   * then maps face labels to speakers using the provided speaker mapping.
   *
   * Optimized: Iterates through sorted speaker intervals and uses binary search
   * to find face detections within each interval, avoiding O(n*m) complexity.
   *
   * @param faceDetections All face detections from the video (assumed sorted by time)
   * @param speakerMapping Mapping from face labels to speaker labels (e.g., { "face1": "spk_0", "face2": "spk_1" })
   * @returns Object with speaker labels as keys and arrays of their active face detections as values
   */
  calculateActiveSpeakerFaceDetections(
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
  ): ActiveSpeakerFaceDetections {
    const result: ActiveSpeakerFaceDetections = {};

    // Get transcript elements to extract speaker intervals
    const originalTranscript = this.editor.getOriginalTranscript();
    if (!originalTranscript || originalTranscript.length === 0) {
      logger.warn(
        'No transcript available for active speaker face detection calculation',
      );
      return result;
    }

    // Extract speaker intervals from transcript (already sorted by time)
    const speakerIntervals = this.extractSpeakerIntervals(originalTranscript);
    if (speakerIntervals.length === 0) {
      logger.warn('No speaker intervals found in transcript');
      return result;
    }

    logger.debug(
      `Found ${speakerIntervals.length} speaker intervals from transcript`,
    );

    // Create reverse mapping: speaker label -> face labels that belong to this speaker
    const speakerToFaceLabels = new Map<string, Set<string>>();
    for (const [faceLabel, speakerLabel] of Object.entries(speakerMapping)) {
      const existingSet = speakerToFaceLabels.get(speakerLabel);
      if (existingSet) {
        existingSet.add(faceLabel);
      } else {
        speakerToFaceLabels.set(speakerLabel, new Set([faceLabel]));
      }
    }

    // Ensure face detections are sorted by time
    const sortedDetections = [...faceDetections].sort(
      (a, b) => a.time - b.time,
    );

    // Binary search to find the first detection index >= startTime
    const findFirstDetectionIndex = (startTime: number): number => {
      let left = 0;
      let right = sortedDetections.length;
      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (sortedDetections[mid].time < startTime) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }
      return left;
    };

    // Iterate through each speaker interval and collect matching face detections
    for (const interval of speakerIntervals) {
      const { speakerLabel, startTime, endTime } = interval;

      // Get the face labels that belong to this speaker
      const faceLabels = speakerToFaceLabels.get(speakerLabel);
      if (!faceLabels || faceLabels.size === 0) {
        // No faces mapped to this speaker, skip
        continue;
      }

      // Find the starting index for detections in this interval
      const startIdx = findFirstDetectionIndex(startTime);

      // Collect all detections within the interval that belong to this speaker's faces
      for (let i = startIdx; i < sortedDetections.length; i++) {
        const detection = sortedDetections[i];

        // Stop if we've passed the interval end
        if (detection.time > endTime) {
          break;
        }

        // Check if this detection's face belongs to the current speaker
        if (faceLabels.has(detection.label)) {
          if (!result[speakerLabel]) {
            result[speakerLabel] = [];
          }
          result[speakerLabel].push(detection);
        }
      }
    }

    // Log summary
    const speakerCounts = Object.entries(result)
      .map(([speaker, detections]) => `${speaker}: ${detections.length}`)
      .join(', ');
    logger.debug(`Active speaker face detections: ${speakerCounts}`);

    return result;
  }

  applyCropBoxesToScene(cropBoxes: Crop[], scene: IScene): void {
    if (!this.editor || cropBoxes.length === 0) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sceneAny = scene;
    if (!sceneAny.layout?.bindings) return;

    const bindings = Object.entries(sceneAny.layout.bindings);

    bindings.forEach(([, binding], index: number) => {
      if (index >= cropBoxes.length) return;

      const cropBox = cropBoxes[index];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bindingAny = binding;

      if (!bindingAny.transformKeyframes) {
        bindingAny.transformKeyframes = [];
      }

      let keyframe = bindingAny.transformKeyframes[0];

      if (!keyframe) {
        logger.warn(
          'No transform keyframe found for binding to apply crop box',
        );
        return;
      } else {
        keyframe.crop = {
          x: cropBox.x,
          y: cropBox.y,
          w: cropBox.width,
          h: cropBox.height,
        };
      }
    });
  }

  autoFrameSingleSceneToActiveSpeaker(
    sceneInfo: {
      scene: Scene;
      startTime: number;
      endTime: number;
    },
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
  ): boolean {
    const activeSpeakerFaceDetections =
      this.calculateActiveSpeakerFaceDetections(faceDetections, speakerMapping);
    const activeSpeakerFaceDetectionsList = Object.values(
      activeSpeakerFaceDetections,
    ).flat();
    return this.autoFrameSingleSceneCommand(
      sceneInfo,
      activeSpeakerFaceDetectionsList,
    );
  }

  autoFrameSingleSceneCommand(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    targetAspectRatio?: number,
  ): boolean {
    const command: Command = {
      getName: () => 'Auto-Frame Single Scene',
      execute: () => {
        this.autoFrameSingleScene(sceneInfo, faceDetections, targetAspectRatio);
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      getDescription: () => 'Auto-Frame Single Scene',
      shouldCreateUndoRedoEntry: () => true,
    };
    this.editor.executeCommand(command);
    logger.debug('Auto-framed single scene via command');
    return true;
  }

  autoFrameSingleScene(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    targetAspectRatio?: number,
  ): boolean {
    const { scene, startTime, endTime } = sceneInfo;

    const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
    if (!transcriptTrackId) {
      logger.error('No transcript track found to autoframe scene for');
      return false;
    }
    if (!faceDetections || faceDetections.length === 0) {
      logger.error('No face detections available');
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overlappingElements = this.sceneService.findElementsInScene(
      this.editor.timeline,
      scene,
      transcriptTrackId,
    ) as any as VideoTranscriptElement[];

    const sceneFaceDetections = overlappingElements.flatMap(el =>
      this.getDetectionsForElement(faceDetections, el).filter(
        detection => detection.time >= startTime && detection.time < endTime,
      ),
    );

    if (sceneFaceDetections.length === 0) {
      logger.warn(`No face detections in scene at ${startTime}s`);
      return false;
    }

    const clusters = this.clusterFaceBoxes(sceneFaceDetections);
    logger.debug('Clusters for scene', clusters);

    const representativeCluster = clusters.sort(
      (a, b) => b.boxes.length - a.boxes.length,
    )[0];

    const cropBoxes = clusters
      .filter(cluster => {
        if (cluster === representativeCluster) return true;
        const distance_x = Math.abs(cluster.avgX - representativeCluster.avgX);

        if (distance_x > CLUSTER_DISTANCE_THRESHOLD) {
          return true;
        }
      })
      .map(cluster =>
        this.calculateCropBoxForCluster(
          cluster,
          this.getCropAspectRatioForLayout(scene.layout, targetAspectRatio),
          this.getCropZoomForLayout(scene.layout),
        ),
      );
    cropBoxes.sort((a, b) => b.width * b.height - a.width * a.height);

    if (cropBoxes.length > 0) {
      this.applyCropBoxesToScene(cropBoxes, scene);
      return true;
    }

    return false;
  }

  // =========================================================================
  // Cluster Selection Strategies
  // =========================================================================

  /**
   * Default strategy: pick the cluster with the most detections (boxes).
   */
  private selectClusterByFrequency(clusters: ClusterInfo[]): ClusterInfo {
    return clusters.sort((a, b) => b.boxes.length - a.boxes.length)[0];
  }

  /**
   * Speaker-aware strategy: iterate ranked speakers (by speaking duration),
   * find the first with one of their face_labels in a cluster, fall back to
   * frequency.
   *
   * A cluster "matches" a face_label if any detection in that cluster's boxes
   * originated from a FaceDetection with that label. Since clusters are built
   * from spatial overlap of boxes, we need to check the original detections.
   * When a speaker has several face_labels in the scene, the one with the most
   * detections wins, framed on the cluster holding most of its boxes.
   */
  private selectClusterBySpeakerRanking(
    clusters: ClusterInfo[],
    sceneFaceDetections: FaceDetection[],
    rankedSpeakers: Array<{ speakerLabel: string }>,
    speakerLabelToFaceLabels: Record<string, string[]>,
  ): ClusterInfo {
    const clusterByDetectionIndex = new Map<number, ClusterInfo>();
    for (const cluster of clusters) {
      for (const srcIdx of cluster.boxIndices) {
        clusterByDetectionIndex.set(srcIdx, cluster);
      }
    }

    // face_label → detection count per cluster it appears in.
    const clusterCountsByFaceLabel = new Map<
      string,
      Map<ClusterInfo, number>
    >();
    for (let i = 0; i < sceneFaceDetections.length; i++) {
      const det = sceneFaceDetections[i];
      const cluster = clusterByDetectionIndex.get(i);
      if (!cluster) continue;

      let counts = clusterCountsByFaceLabel.get(det.label);
      if (!counts) {
        counts = new Map();
        clusterCountsByFaceLabel.set(det.label, counts);
      }
      counts.set(cluster, (counts.get(cluster) ?? 0) + 1);
    }

    // Iterate ranked speakers, resolve to face_labels, find matching cluster
    for (const { speakerLabel } of rankedSpeakers) {
      let best: { cluster: ClusterInfo; count: number } | null = null;
      for (const faceLabel of speakerLabelToFaceLabels[speakerLabel] ?? []) {
        for (const [cluster, count] of clusterCountsByFaceLabel.get(
          faceLabel,
        ) ?? []) {
          if (!best || count > best.count) best = { cluster, count };
        }
      }

      if (best) {
        logger.debug(
          `selectClusterBySpeakerRanking: picked cluster for speaker=${speakerLabel} ` +
            `(${best.count} of its detections, ${best.cluster.boxes.length} boxes)`,
        );
        return best.cluster;
      }
    }

    // Fallback: frequency-based
    logger.debug(
      'selectClusterBySpeakerRanking: no speaker→face→cluster match, falling back to frequency ' +
        JSON.stringify({
          rankedSpeakers: rankedSpeakers.map(({ speakerLabel }) => ({
            speakerLabel,
            faces: speakerLabelToFaceLabels[speakerLabel] ?? [],
          })),
          sceneFaces: [...clusterCountsByFaceLabel.keys()],
        }),
    );
    return this.selectClusterByFrequency(clusters);
  }

  /**
   * Auto-frame a single scene using speaker-ranked cluster selection.
   *
   * Uses transcript speaker data to rank speakers by duration within the scene,
   * resolves each to a face_label via the pre-built mapping, and prefers the
   * matching cluster as the crop target. Falls back through ranked speakers,
   * then to frequency-based selection.
   *
   * @param sceneInfo Scene with resolved timeline times
   * @param faceDetections All face detections (raw, in media time)
   * @param speakerLabelToFaceLabels Pre-built map: speaker_label → face_labels
   * @param targetAspectRatio Optional target aspect ratio
   */
  autoFrameSingleSceneWithSpeakerContext(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerLabelToFaceLabels: Record<string, string[]>,
    targetAspectRatio?: number,
  ): boolean {
    const framing = this.computeSpeakerContextFraming(
      sceneInfo,
      faceDetections,
      speakerLabelToFaceLabels,
      targetAspectRatio,
    );
    if (!framing) return false;
    this.applyCropBoxesToScene(framing.cropBoxes, sceneInfo.scene);
    return true;
  }

  /**
   * The crops `autoFrameSingleSceneWithSpeakerContext` would apply, without
   * touching the scene — for callers that batch several scenes into one
   * command (the reframe modal's speaker apply, PLAT-11992).
   *
   * `speakerLabel` / `faceLabels` say whom the representative crop framed
   * (null speaker when the scene fell back to face frequency).
   */
  computeSpeakerContextFraming(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerLabelToFaceLabels: Record<string, string[]>,
    targetAspectRatio?: number,
  ): {
    cropBoxes: Crop[];
    /** Who talks most in the scene, framed or not. */
    topSpeaker: string | null;
    speakerLabel: string | null;
    faceLabels: string[];
  } | null {
    const { scene, startTime, endTime } = sceneInfo;

    const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
    if (!transcriptTrackId) {
      logger.error('No transcript track found to autoframe scene for');
      return null;
    }
    if (!faceDetections || faceDetections.length === 0) {
      logger.error('No face detections available');
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overlappingElements = this.sceneService.findElementsInScene(
      this.editor.timeline,
      scene,
      transcriptTrackId,
    ) as any as VideoTranscriptElement[];

    const sceneFaceDetections = overlappingElements.flatMap(el =>
      this.getDetectionsForElement(faceDetections, el).filter(
        detection => detection.time >= startTime && detection.time < endTime,
      ),
    );

    if (sceneFaceDetections.length === 0) {
      logger.warn(`No face detections in scene at ${startTime}s`);
      return null;
    }

    const clusters = this.clusterFaceBoxes(sceneFaceDetections);
    if (clusters.length === 0) return null;

    // Rank speakers for this scene. The transcript carries timestamps in
    // **media time** (original source), but `startTime`/`endTime` are in
    // **timeline time** (resolved via layoutEngine). For any story with
    // cuts/reorders these diverge, so we remap each word using the same
    // per-element mapping that `getDetectionsForElement` uses for faces.
    const originalTranscript = this.editor.getOriginalTranscript();
    const transcriptInTimelineTime =
      originalTranscript && originalTranscript.length > 0
        ? overlappingElements.flatMap(el => {
            const elMediaStart = el.getMediaStartTime();
            const elMediaEnd = el.getMediaEndTime();
            return originalTranscript
              .filter(
                w =>
                  w.ts != null &&
                  w.end_ts != null &&
                  w.ts >= elMediaStart &&
                  w.end_ts <= elMediaEnd,
              )
              .map(w => ({
                ...w,
                ts: el.time + (w.ts as number) - elMediaStart,
                end_ts: el.time + (w.end_ts as number) - elMediaStart,
              }));
          })
        : [];
    const rankedSpeakers =
      transcriptInTimelineTime.length > 0
        ? rankSpeakersForScene(transcriptInTimelineTime, startTime, endTime)
        : [];

    // Select representative cluster using speaker-aware strategy
    let representativeCluster: ClusterInfo;
    let framedSpeaker: string | null = null;
    const topSpeaker = rankedSpeakers[0]?.speakerLabel;
    const hasMap = Object.keys(speakerLabelToFaceLabels).length > 0;
    const topSpeakerLabels = new Set(
      topSpeaker ? (speakerLabelToFaceLabels[topSpeaker] ?? []) : [],
    );
    if (
      hasMap &&
      topSpeaker &&
      !sceneFaceDetections.some(d => topSpeakerLabels.has(d.label))
    ) {
      // The speaker's own face isn't known here: frame whoever isn't one of
      // the other known speakers (PLAT-11992, widened from two-person videos
      // in PLAT-12143 — in a panel where everyone else is mapped, that leaves
      // only the speaker). Falling through the ranking instead would land on
      // a known speaker who isn't talking.
      const otherLabels = new Set(
        Object.entries(speakerLabelToFaceLabels)
          .filter(([speaker]) => speaker !== topSpeaker)
          .flatMap(([, labels]) => labels),
      );
      const excluded = selectClusterExcludingLabels(
        clusters,
        sceneFaceDetections,
        otherLabels,
        // Two people: "not A" is B. More: only a single leftover face counts.
        !originalTranscript ||
          this.timelineSpeakersFor(originalTranscript).length !== 2,
      );
      representativeCluster =
        excluded ?? this.selectClusterByFrequency([...clusters]);
      if (excluded) framedSpeaker = topSpeaker;
    } else if (rankedSpeakers.length > 0 && hasMap) {
      representativeCluster = this.selectClusterBySpeakerRanking(
        clusters,
        sceneFaceDetections,
        rankedSpeakers,
        speakerLabelToFaceLabels,
      );
      framedSpeaker =
        rankedSpeakers.find(({ speakerLabel }) =>
          (speakerLabelToFaceLabels[speakerLabel] ?? []).some(label =>
            representativeCluster.boxIndices.some(
              index => sceneFaceDetections[index].label === label,
            ),
          ),
        )?.speakerLabel ?? null;
    } else {
      representativeCluster = this.selectClusterByFrequency(clusters);
    }

    // applyCropBoxesToScene maps cropBoxes[i] → binding[i] by index, so the
    // representative must be first regardless of where the clustering put it
    // in the `clusters` array. (selectClusterByFrequency happens to sort
    // clusters in place; selectClusterBySpeakerRanking does not.)
    const otherClusters = clusters.filter(
      cluster =>
        cluster !== representativeCluster &&
        Math.abs(cluster.avgX - representativeCluster.avgX) >
          CLUSTER_DISTANCE_THRESHOLD,
    );
    const cropBoxes = [representativeCluster, ...otherClusters].map(cluster =>
      this.calculateCropBoxForCluster(
        cluster,
        this.getCropAspectRatioForLayout(scene.layout, targetAspectRatio),
        this.getCropZoomForLayout(scene.layout),
      ),
    );

    const faceLabels = [
      ...new Set(
        representativeCluster.boxIndices.map(
          index => sceneFaceDetections[index].label,
        ),
      ),
    ];
    // One line per scene so a wrong crop can be traced to its cause.
    const facesOnScreen: Record<string, number> = {};
    for (const { label } of sceneFaceDetections) {
      facesOnScreen[label] = (facesOnScreen[label] ?? 0) + 1;
    }
    logger.debug(
      `Scene framing ${convertTimeToMMSS(startTime)}–${convertTimeToMMSS(endTime)} ` +
        JSON.stringify({
          speakers: rankedSpeakers.map(({ speakerLabel, totalDuration }) => ({
            speakerLabel,
            seconds: Number(totalDuration.toFixed(1)),
            mappedFaces: speakerLabelToFaceLabels[speakerLabel] ?? [],
          })),
          facesOnScreen,
          framedFaces: faceLabels,
          framedSpeaker,
          cropX: Number(cropBoxes[0]?.x.toFixed(2)),
        }),
    );

    return {
      cropBoxes,
      topSpeaker: topSpeaker ?? null,
      speakerLabel: framedSpeaker,
      faceLabels,
    };
  }

  /** Every scene with its resolved timeline range. */
  getSceneInfos(): Array<{ scene: Scene; startTime: number; endTime: number }> {
    return this.editor.timeline.scenes.map(scene => ({
      scene,
      startTime: this.layoutEngine.resolveAnchor(
        scene.anchorStart,
        this.editor.timeline,
      ),
      endTime: this.layoutEngine.resolveAnchor(
        scene.anchorEnd,
        this.editor.timeline,
      ),
    }));
  }

  /** The scene's face detections, remapped to timeline time. */
  getSceneFaceDetections(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
  ): FaceDetection[] {
    const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
    if (!transcriptTrackId) return [];
    const overlappingElements = this.sceneService.findElementsInScene(
      this.editor.timeline,
      sceneInfo.scene,
      transcriptTrackId,
    ) as unknown as VideoTranscriptElement[];
    return overlappingElements.flatMap(el =>
      this.getDetectionsForElement(faceDetections, el).filter(
        detection =>
          detection.time >= sceneInfo.startTime &&
          detection.time < sceneInfo.endTime,
      ),
    );
  }

  /**
   * Distinct transcript speakers in the media the timeline uses — "is this a
   * two-person video" for the clip or edit at hand, not the whole interview.
   */
  timelineSpeakers(): readonly string[] {
    const originalTranscript = this.editor.getOriginalTranscript();
    return originalTranscript
      ? this.timelineSpeakersFor(originalTranscript)
      : [];
  }

  private timelineSpeakersFor(
    originalTranscript: ReadonlyArray<{
      ts?: number | null;
      end_ts?: number | null;
      speaker_label?: string;
      type?: string;
    }>,
  ): readonly string[] {
    const ranges = (this.editor.getVideoTranscriptTrack()?.elements ?? []).map(
      el => {
        const element = el as unknown as VideoTranscriptElement;
        return [element.getMediaStartTime(), element.getMediaEndTime()];
      },
    );
    // Called once per scene by the all-scenes loop; the answer only changes
    // when the cuts or the transcript do.
    const signature = ranges.map(([start, end]) => `${start}-${end}`).join();
    const cached = this.timelineSpeakersCache;
    if (
      cached?.transcript === originalTranscript &&
      cached.signature === signature
    ) {
      return cached.speakers;
    }

    const speakers = new Set<string>();
    for (const word of originalTranscript) {
      if (word.type !== 'text' || word.ts == null || !word.speaker_label) {
        continue;
      }
      const ts = word.ts;
      if (ranges.some(([start, end]) => ts >= start && ts < end)) {
        speakers.add(word.speaker_label);
      }
    }
    this.timelineSpeakersCache = {
      transcript: originalTranscript,
      signature,
      speakers: [...speakers],
    };
    return this.timelineSpeakersCache.speakers;
  }

  /**
   * Command-wrapped variant for UI callers. The raw
   * `*WithSpeakerContext` mutates the scene directly without notifying
   * observers — safe inside another Command (the All-scenes loop) but
   * invisible to the renderer when called on its own.
   */
  autoFrameSingleSceneWithSpeakerContextCommand(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerLabelToFaceLabels: Record<string, string[]>,
    targetAspectRatio?: number,
  ): boolean {
    let success = false;
    const command: Command = {
      getName: () => 'Auto-Frame Single Scene (Speaker Context)',
      execute: () => {
        success = this.autoFrameSingleSceneWithSpeakerContext(
          sceneInfo,
          faceDetections,
          speakerLabelToFaceLabels,
          targetAspectRatio,
        );
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      shouldCreateUndoRedoEntry: () => true,
      getDescription: () => 'Auto-Frame Single Scene (Speaker Context)',
    };
    this.editor.executeCommand(command);
    return success;
  }

  /**
   * Auto-frame all scenes using speaker-ranked cluster selection.
   * Wraps the entire operation in a single Command for undo/redo.
   */
  autoFrameAllScenesWithSpeakerContext(
    faceDetections: FaceDetection[],
    speakerLabelToFaceLabels: Record<string, string[]>,
    targetAspectRatio?: number,
  ): number {
    if (!faceDetections || faceDetections.length === 0) {
      logger.warn('No face detections available');
      return 0;
    }

    if (
      !this.editor?.timeline.scenes ||
      this.editor.timeline.scenes.length === 0
    ) {
      logger.warn('No scenes found in timeline');
      return 0;
    }

    let framedCount = 0;
    const totalScenes = this.editor.timeline.scenes.length;

    const command: Command = {
      getName: () => 'Auto-Frame All Scenes (Speaker Context)',
      execute: () => {
        // Whom the last framed scene showed: a scene whose speaker has no
        // face (a background voice) stays on that person while they're still
        // on screen, rather than cutting to a guess (PLAT-12143).
        let previousFaces: string[] = [];
        const sceneInfos = this.getSceneInfos().sort(
          (a, b) => a.startTime - b.startTime,
        );
        for (const sceneInfo of sceneInfos) {
          let framing = this.computeSpeakerContextFraming(
            sceneInfo,
            faceDetections,
            speakerLabelToFaceLabels,
            targetAspectRatio,
          );
          if (!framing) continue;

          if (
            framing.speakerLabel === null &&
            framing.topSpeaker &&
            previousFaces.length > 0
          ) {
            const held = this.computeSpeakerContextFraming(
              sceneInfo,
              faceDetections,
              {
                ...speakerLabelToFaceLabels,
                [framing.topSpeaker]: previousFaces,
              },
              targetAspectRatio,
            );
            if (held?.faceLabels.some(label => previousFaces.includes(label))) {
              logger.debug(
                `Scene framing ${convertTimeToMMSS(sceneInfo.startTime)}: ` +
                  `${framing.topSpeaker} has no face; kept ${previousFaces.join(',')}`,
              );
              framing = held;
            }
          }

          this.applyCropBoxesToScene(framing.cropBoxes, sceneInfo.scene);
          previousFaces = framing.faceLabels;
          framedCount++;
        }
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      shouldCreateUndoRedoEntry: () => true,
      getDescription: () => 'Auto-Frame All Scenes (Speaker Context)',
    };

    this.editor.executeCommand(command);
    logger.info(
      `Auto-framed (speaker context) ${framedCount} of ${totalScenes} scenes`,
    );

    return framedCount;
  }

  autoLayoutScene(
    scene: Scene,
    faceDetections: FaceDetection[],
  ): { sceneId: string; updates: Partial<IScene> } | null {
    const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
    if (!transcriptTrackId) {
      logger.error('No transcript track found to autoframe scene for');
      return null;
    }
    if (!faceDetections || faceDetections.length === 0) {
      logger.error('No face detections available');
      return null;
    }

    const templateRegistry = LayoutTemplateRegistry.getInstance();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overlappingElements = this.sceneService.findElementsInScene(
      this.editor.timeline,
      scene,
      transcriptTrackId,
    ) as any as VideoTranscriptElement[];

    const startTime = this.layoutEngine.resolveAnchor(
      scene.anchorStart,
      this.editor.timeline,
    );
    const endTime = this.layoutEngine.resolveAnchor(
      scene.anchorEnd,
      this.editor.timeline,
    );

    const sceneFaceDetections = overlappingElements.flatMap(el =>
      this.getDetectionsForElement(faceDetections, el).filter(
        detection => detection.time >= startTime && detection.time < endTime,
      ),
    );

    if (sceneFaceDetections.length === 0) {
      logger.warn(`No face detections in scene at ${startTime}s`);
      return null;
    }

    const clusters = this.clusterFaceBoxes(sceneFaceDetections);

    let nextTemplateId;
    if (clusters.length >= 2) {
      if (
        clusters.find(cluster1 =>
          clusters.find(cluster2 => {
            if (cluster1 === cluster2) return false;
            const distance_x = Math.abs(cluster1.avgX - cluster2.avgX);
            if (distance_x > CLUSTER_DISTANCE_THRESHOLD) {
              return true;
            }
          }),
        )
      ) {
        nextTemplateId = TemplateIds.SplitHorizontal;
      } else {
        nextTemplateId = TemplateIds.Cover;
      }
    } else {
      nextTemplateId = TemplateIds.Cover;
    }

    if (nextTemplateId === scene.layout.layoutTemplateId) {
      return null;
    }

    const nextTemplate = templateRegistry.getTemplate(nextTemplateId);
    if (!nextTemplate) {
      logger.warn(`Template ${nextTemplateId} not found`);
      return null;
    }

    const nextBindings = buildBindingsForTemplate(
      nextTemplate,
      scene.layout.bindings,
      this.editor.getDefaultKaraokeConfig(),
    );

    if (!nextBindings) {
      logger.warn(
        `Scene ${scene.id} does not have bindings to clone for template change`,
      );
      return null;
    }

    const layoutJson = scene.layout.toJSON();
    return {
      sceneId: scene.id,
      updates: {
        layout: {
          ...layoutJson,
          layoutTemplateId: nextTemplateId,
          bindings: nextBindings,
        },
      },
    };
  }

  autoLayoutAllScenes(
    faceDetections: FaceDetection[],
  ): Array<{ sceneId: string; updates: Partial<IScene> }> {
    if (!faceDetections || faceDetections.length === 0) {
      logger.error('No face detections available');
      return [];
    }

    const scenes = this.editor.timeline.scenes;
    if (!scenes || scenes.length === 0) {
      logger.warn('No scenes to auto-layout');
      return [];
    }

    const updates: Array<{ sceneId: string; updates: Partial<IScene> }> = [];

    for (const scene of scenes) {
      const update = this.autoLayoutScene(scene, faceDetections);
      if (update) {
        updates.push(update);
      }
    }

    if (updates.length === 0) {
      logger.info('No scenes needed layout updates');
    }

    return updates;
  }

  autoFrameAllScenesToActiveSpeaker(
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
  ): number {
    const activeSpeakerFaceDetections =
      this.calculateActiveSpeakerFaceDetections(faceDetections, speakerMapping);
    const activeSpeakerFaceDetectionsList = Object.values(
      activeSpeakerFaceDetections,
    ).flat();
    return this.autoFrameAllScenes(activeSpeakerFaceDetectionsList);
  }

  // =========================================================================
  // Phase 1 – Interval-aware framing (PLAT-6535)
  // =========================================================================

  /**
   * Build `SceneFramingInterval[]` for a single scene by combining transcript
   * speaker data with face detections and speaker→face mapping.
   *
   * Encapsulates editor access (original transcript) so the pure
   * `buildSceneFramingIntervals` function stays side-effect-free.
   */
  getSceneFramingIntervals(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
  ): SceneFramingInterval[] {
    const originalTranscript = this.editor.getOriginalTranscript();
    if (!originalTranscript || originalTranscript.length === 0) {
      logger.warn(
        'No transcript available for scene framing interval calculation',
      );
      return [];
    }

    const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
    if (!transcriptTrackId) {
      logger.error('No transcript track found to apply face detections');
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overlappingElements = this.sceneService.findElementsInScene(
      this.editor.timeline,
      sceneInfo.scene,
      transcriptTrackId,
    ) as any as VideoTranscriptElement[];

    // Resolve scene bounds in timeline time (= original video source time
    // as positioned on the composition timeline).
    const sceneStart = this.layoutEngine.resolveAnchor(
      sceneInfo.scene.anchorStart,
      this.editor.timeline,
    );
    const sceneEnd = this.layoutEngine.resolveAnchor(
      sceneInfo.scene.anchorEnd,
      this.editor.timeline,
    );

    // Face detections remapped from media-time → timeline-time, clipped to scene.
    const sceneFaceDetections = overlappingElements.flatMap(el =>
      this.getDetectionsForElement(faceDetections, el).filter(
        detection => detection.time >= sceneStart && detection.time < sceneEnd,
      ),
    );

    // Speaker intervals are in media-time; remap to timeline-time via the
    // same overlapping elements so they share the face-detection time domain.
    const rawSpeakerIntervals =
      this.extractSpeakerIntervals(originalTranscript);
    if (rawSpeakerIntervals.length === 0) {
      logger.warn('No speaker intervals found in transcript');
      return [];
    }

    const speakerIntervals = this.remapSpeakerIntervalsToTimeline(
      rawSpeakerIntervals,
      overlappingElements,
    );
    if (speakerIntervals.length === 0) {
      logger.warn(
        'No speaker intervals overlap with scene elements after remapping',
      );
      return [];
    }

    logger.debug(
      `getSceneFramingIntervals: scene=${sceneInfo.scene.id} ` +
        `resolved=[${sceneStart.toFixed(2)}–${sceneEnd.toFixed(2)}s], ` +
        `speakerIntervals=${speakerIntervals.length} (raw=${rawSpeakerIntervals.length}), ` +
        `sceneFaceDetections=${sceneFaceDetections.length}, ` +
        `mappingKeys=${Object.keys(speakerMapping).length}`,
    );

    const intervals = buildSceneFramingIntervals(
      speakerIntervals,
      sceneFaceDetections,
      speakerMapping,
      { start: sceneStart, end: sceneEnd },
    );

    logger.debug(
      `getSceneFramingIntervals: scene=${sceneInfo.scene.id} → ` +
        `${intervals.length} intervals: ` +
        intervals
          .map(i => `${i.type}(${i.faceDetections.length}dets)`)
          .join(', '),
    );

    return intervals;
  }

  /**
   * Auto-frame a single scene using interval classification.
   * Picks the longest interval's face detections and delegates to the
   * existing `autoFrameSingleScene`.  Falls back to the full detection
   * list when intervals yield nothing.
   */
  autoFrameSingleSceneWithIntervals(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
    targetAspectRatio?: number,
  ): boolean {
    const intervals = this.getSceneFramingIntervals(
      sceneInfo,
      faceDetections,
      speakerMapping,
    );

    const longestDetections = getLongestIntervalDetections(intervals);

    if (longestDetections.length > 0) {
      const primaryFace = longestDetections[0]?.label ?? '?';
      logger.debug(
        `autoFrameSingleSceneWithIntervals: scene=${sceneInfo.scene.id} ` +
          `→ using ${longestDetections.length} detections from longest interval, ` +
          `primaryFace=${primaryFace}`,
      );
      return this.autoFrameSingleScene(
        sceneInfo,
        longestDetections,
        targetAspectRatio,
      );
    }

    // Fallback: use full detection list (same as non-interval path).
    logger.debug(
      `autoFrameSingleSceneWithIntervals: scene=${sceneInfo.scene.id} ` +
        `→ interval detections empty (${intervals.length} intervals), ` +
        `falling back to full detection list (${faceDetections.length} dets)`,
    );
    return this.autoFrameSingleScene(
      sceneInfo,
      faceDetections,
      targetAspectRatio,
    );
  }

  /**
   * Command-wrapped variant for UI callers. The raw `*WithIntervals` mutates
   * the scene directly without notifying observers — fine when invoked from
   * inside another Command (the All-scenes loop) but invisible to the
   * renderer when called on its own.
   */
  autoFrameSingleSceneWithIntervalsCommand(
    sceneInfo: { scene: Scene; startTime: number; endTime: number },
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
    targetAspectRatio?: number,
  ): boolean {
    let success = false;
    const command: Command = {
      getName: () => 'Auto-Frame Single Scene (Intervals)',
      execute: () => {
        success = this.autoFrameSingleSceneWithIntervals(
          sceneInfo,
          faceDetections,
          speakerMapping,
          targetAspectRatio,
        );
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      shouldCreateUndoRedoEntry: () => true,
      getDescription: () => 'Auto-Frame Single Scene (Intervals)',
    };
    this.editor.executeCommand(command);
    return success;
  }

  /**
   * Auto-frame all scenes using interval classification per scene.
   * Wraps the entire operation in a single Command for undo/redo.
   */
  autoFrameAllScenesWithIntervals(
    faceDetections: FaceDetection[],
    speakerMapping: FaceToSpeakerLabelMapping,
    targetAspectRatio?: number,
  ): number {
    if (!faceDetections || faceDetections.length === 0) {
      logger.warn('No face detections available');
      return 0;
    }

    if (
      !this.editor?.timeline.scenes ||
      this.editor.timeline.scenes.length === 0
    ) {
      logger.warn('No scenes found in timeline');
      return 0;
    }

    let framedCount = 0;
    const totalScenes = this.editor.timeline.scenes.length;

    const command: Command = {
      getName: () => 'Auto-Frame All Scenes (Intervals)',
      execute: () => {
        for (const scene of this.editor.timeline.scenes) {
          const startTime = this.layoutEngine.resolveAnchor(
            scene.anchorStart,
            this.editor.timeline,
          );
          const endTime = this.layoutEngine.resolveAnchor(
            scene.anchorEnd,
            this.editor.timeline,
          );

          const sceneInfo = { scene, startTime, endTime };

          const success = this.autoFrameSingleSceneWithIntervals(
            sceneInfo,
            faceDetections,
            speakerMapping,
            targetAspectRatio,
          );
          if (success) {
            framedCount++;
          }
        }
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      shouldCreateUndoRedoEntry: () => true,
      getDescription: () => 'Auto-Frame All Scenes (Intervals)',
    };

    this.editor.executeCommand(command);
    logger.info(
      `Auto-framed (intervals) ${framedCount} of ${totalScenes} scenes`,
    );

    return framedCount;
  }

  autoFrameAllScenes(
    faceDetections: FaceDetection[],
    targetAspectRatio?: number,
  ): number {
    if (!faceDetections || faceDetections.length === 0) {
      logger.warn('No face detections available');
      return 0;
    }

    if (
      !this.editor?.timeline.scenes ||
      this.editor.timeline.scenes.length === 0
    ) {
      logger.warn('No scenes found in timeline');
      return 0;
    }

    let framedCount = 0;
    const totalScenes = this.editor.timeline.scenes.length;

    const command: Command = {
      getName: () => 'Auto-Frame All Scenes',
      execute: () => {
        for (const scene of this.editor.timeline.scenes) {
          const startTime = this.layoutEngine.resolveAnchor(
            scene.anchorStart,
            this.editor.timeline,
          );
          const endTime = this.layoutEngine.resolveAnchor(
            scene.anchorEnd,
            this.editor.timeline,
          );

          const sceneInfo = {
            scene,
            startTime,
            endTime,
          };

          const success = this.autoFrameSingleScene(
            sceneInfo,
            faceDetections,
            targetAspectRatio,
          );
          if (success) {
            framedCount++;
          }
        }
        this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
          timeline: this.editor.timeline,
        });
        return true;
      },
      undo: () => false,
      shouldCreateUndoRedoEntry: () => true,
      getDescription: () => 'Auto-Frame All Scenes',
    };

    this.editor.executeCommand(command);
    logger.info(`Auto-framed ${framedCount} of ${totalScenes} scenes`);

    return framedCount;
  }

  showCropBoxes916(faceDetections: FaceDetection[]): void {
    if (!faceDetections || faceDetections.length === 0) {
      logger.warn('No face detections available');
      return;
    }

    if (
      !this.editor?.timeline.scenes ||
      this.editor.timeline.scenes.length === 0
    ) {
      logger.warn('No scenes found in timeline');
      return;
    }

    const command: Command = {
      getName: () => 'ShowCropBoxes916',
      execute: () => {
        const aspectRatioValue = 9 / 16;
        const transcriptTrackId = this.editor.getVideoTranscriptTrack()?.id;
        if (!transcriptTrackId) {
          logger.error('No transcript track found to apply face detections');
          return false;
        }
        let shapeCount = 0;

        for (const scene of this.editor.timeline.scenes) {
          const startTime = this.layoutEngine.resolveAnchor(
            scene.anchorStart,
            this.editor.timeline,
          );
          const endTime = this.layoutEngine.resolveAnchor(
            scene.anchorEnd,
            this.editor.timeline,
          );

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const overlappingElements = this.sceneService.findElementsInScene(
            this.editor.timeline,
            scene,
            transcriptTrackId,
          ) as any as VideoTranscriptElement[];

          const sceneFaceDetections = overlappingElements.flatMap(el =>
            this.getDetectionsForElement(faceDetections, el).filter(
              detection =>
                detection.time >= startTime && detection.time <= endTime,
            ),
          );

          if (sceneFaceDetections.length === 0) {
            continue;
          }

          logger.debug('sceneFaceDetections:', sceneFaceDetections);

          const clusters = this.clusterFaceBoxes(sceneFaceDetections);

          logger.debug(
            'Clusters for scene',
            scene.layout.layoutTemplateId,
            clusters,
          );

          const cropBoxes: Crop[] = clusters
            .sort((a, b) => b.boxes.length - a.boxes.length)
            .map(cluster =>
              this.calculateCropBoxForCluster(cluster, aspectRatioValue, 1),
            );

          logger.debug('Crop boxes for scene', cropBoxes);

          if (cropBoxes.length > 0) {
            this.createCropBoxShapes(
              [cropBoxes[0]],
              startTime,
              endTime - startTime,
              shapeCount,
            );
            shapeCount += cropBoxes.length;
          }
        }

        this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          track: this.editor.timeline.tracks.find(
            (t: any) => t.name === 'Crop Boxes (9:16)',
          ),
        });

        logger.info(`Created ${shapeCount} crop box shape(s) for 9:16`);
        return true;
      },
      undo: () => false,
      getDescription: () => 'Show Crop Boxes for 9:16',
      shouldCreateUndoRedoEntry: () => true,
    };

    this.editor.executeCommand(command);
  }

  private createCropBoxShapes(
    cropBoxes: Crop[],
    startTime: number,
    duration: number,
    colorIndex: number = 0,
  ): void {
    if (!this.editor || cropBoxes.length === 0) return;

    const colors = ['#ff0000', '#0000ff', '#00ff00', '#ffff00', '#ff00ff'];

    cropBoxes.forEach((cropBox, index) => {
      const shapeProps = {
        time: startTime,
        duration: duration,
        x: `${cropBox.x * 100}%`,
        y: `${cropBox.y * 100}%`,
        width: `${cropBox.width * 100}%`,
        height: `${cropBox.height * 100}%`,
        z_index: 100,
        x_anchor: '0%',
        y_anchor: '0%',
        path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
        stroke_color: colors[(colorIndex + index) % colors.length],
        stroke_width: '0.5 vmin',
        fill_color: 'rgba(0,0,0,0)',
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let cropBoxTrack = this.editor.timeline.tracks.find(
        (t: any) => t.name === 'Crop Boxes (9:16)',
      );

      if (!cropBoxTrack) {
        cropBoxTrack = this.editor.timeline.createTrack('shape');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (cropBoxTrack as any).setName('Crop Boxes (9:16)');
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.editor.timeline.addElement(
        (cropBoxTrack as any).id,
        Timeline.createElement('shape', shapeProps),
      );
    });
  }

  addActiveSpeakerFaceDetectionTracks(
    detections: FaceDetection[],
    speakerFaceMapping: Record<string, string>,
  ): void {
    const activeSpeakerFaceDetections =
      this.calculateActiveSpeakerFaceDetections(detections, speakerFaceMapping);
    const flatDetections = Object.values(activeSpeakerFaceDetections).flat();
    this.addFaceDetectionTracks(flatDetections);
  }

  addFaceDetectionTracks(detections: FaceDetection[]): void {
    const palette = [
      '#ff0000',
      '#0000ff',
      '#00ff00',
      '#ffff00',
      '#ff00ff',
      '#00ffff',
    ];
    let colorIndex = 0;
    this._addFaceDetectionTracks(detections, () => {
      const c = palette[colorIndex % palette.length];
      colorIndex++;
      return c;
    });
  }

  /**
   * Render face-detection tracks with bounding-box colour resolved per
   * face_label. Faces with no mapped speaker fall back to `fallbackColor`.
   * When `options.clearOtherTracks` is true, every track except the
   * primary video (transcript) track is removed inside the same Command
   * so the operation is one undo step.
   */
  addFaceDetectionTracksColoredBySpeaker(
    detections: FaceDetection[],
    faceLabelToSpeakerColor: Record<string, string>,
    fallbackColor = '#A9A9A9',
    options: { clearOtherTracks?: boolean } = {},
  ): void {
    this._addFaceDetectionTracks(
      detections,
      label => faceLabelToSpeakerColor[label] ?? fallbackColor,
      options,
    );
  }

  /**
   * Active-speaker variant of `addFaceDetectionTracksColoredBySpeaker`.
   * Filters detections to those marked active per `speakerFaceMapping`
   * (face_label → speaker_label), then colours each track by the speaker
   * via `getSpeakerColor(speaker_label, …)`.
   */
  addActiveSpeakerFaceDetectionTracksColoredBySpeaker(
    detections: FaceDetection[],
    speakerFaceMapping: Record<string, string>,
    fallbackColor = '#A9A9A9',
    options: { clearOtherTracks?: boolean } = {},
  ): void {
    const activeSpeakerFaceDetections =
      this.calculateActiveSpeakerFaceDetections(detections, speakerFaceMapping);
    const flatDetections = Object.values(activeSpeakerFaceDetections).flat();
    const faceLabelToColor: Record<string, string> = {};
    for (const [faceLabel, speakerLabel] of Object.entries(
      speakerFaceMapping,
    )) {
      faceLabelToColor[faceLabel] = getSpeakerColor(speakerLabel, 0);
    }
    this._addFaceDetectionTracks(
      flatDetections,
      label => faceLabelToColor[label] ?? fallbackColor,
      options,
    );
  }

  private _addFaceDetectionTracks(
    detections: FaceDetection[],
    getStrokeColor: (faceLabel: string) => string,
    options: { clearOtherTracks?: boolean } = {},
  ): void {
    if (!detections || detections.length === 0) {
      logger.info('No face detections available');
      return;
    }

    if (!this.editor) {
      logger.error('No video editor available');
      return;
    }

    const transcriptTrackElements =
      this.editor.getVideoTranscriptTrack()?.elements;
    if (!transcriptTrackElements) {
      logger.error('No transcript track found to apply face detections');
      return;
    }

    const detectionsByElement = transcriptTrackElements.map(el =>
      this.getDetectionsForElement(detections, el),
    );

    const flatDetections = detectionsByElement.flat();

    // Group detections by label
    const detectionsByLabel = new Map<string, FaceDetection[]>();
    for (const detection of flatDetections) {
      const label = detection.label || 'unknown';
      if (!detectionsByLabel.has(label)) {
        detectionsByLabel.set(label, []);
      }
      const labelDetections = detectionsByLabel.get(label);
      if (labelDetections) {
        labelDetections.push(detection);
      }
    }

    const command: Command = {
      getName: () => 'AddFaceDetections',
      execute: () => {
        if (options.clearOtherTracks) {
          const primaryTrackId = this.editor.getVideoTranscriptTrack()?.id;
          // Snapshot ids first — removeTrack mutates the underlying map.
          const toRemove = this.editor.timeline.tracks
            .map(t => t.id)
            .filter(id => id !== primaryTrackId);
          for (const id of toRemove) {
            this.editor.timeline.removeTrack(id);
          }
        }
        const tracksByLabel = new Map<string, string>();
        const keysSortedBySize = Array.from(detectionsByLabel.keys()).sort(
          (a, b) =>
            (detectionsByLabel.get(b)?.length || 0) -
            (detectionsByLabel.get(a)?.length || 0),
        );
        const top10NonRare = keysSortedBySize
          .slice(0, 10)
          .filter(label => (detectionsByLabel.get(label)?.length || 0) >= 5);

        const other = keysSortedBySize.filter(
          label => !top10NonRare.includes(label),
        );

        const rareDetections = other.flatMap(
          label => detectionsByLabel.get(label) || [],
        );

        // Create one track per label
        for (const label of top10NonRare) {
          const track = this.editor.timeline.createTrack('shape');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tracksByLabel.set(label, (track as any).id);
          if (Number(track.id) === 31) {
            break;
          }
        }

        // Add detections to their respective tracks
        for (const [label, labelDetections] of detectionsByLabel.entries()) {
          let trackId = tracksByLabel.get(label);
          if (!trackId) continue;

          const strokeColor = getStrokeColor(label);

          // Sort detections by time for proper duration calculation
          const sortedDetections = [...labelDetections].sort(
            (a, b) => a.time - b.time,
          );

          // Iterate over scenes and detections together (both ordered)
          let detectionIndex = 0;

          for (const scene of this.editor.timeline.scenes) {
            const sceneStart = this.layoutEngine.resolveAnchor(
              scene.anchorStart,
              this.editor.timeline,
            );
            const sceneEnd = this.layoutEngine.resolveAnchor(
              scene.anchorEnd,
              this.editor.timeline,
            );

            // Process detections within this scene
            while (
              detectionIndex < sortedDetections.length &&
              sortedDetections[detectionIndex].time < sceneEnd
            ) {
              const detection = sortedDetections[detectionIndex];

              // Skip detections before this scene starts
              if (detection.time < sceneStart) {
                detectionIndex++;
                continue;
              }

              const nextDetection = sortedDetections[detectionIndex + 1];

              const box = {
                x: detection.box[0],
                y: detection.box[1],
                width: detection.box[2],
                height: detection.box[3],
              };

              // Determine duration: extend to next detection if it's also in this scene
              let duration = 0.1; // Default duration
              if (
                nextDetection &&
                nextDetection.time >= sceneStart &&
                nextDetection.time < sceneEnd
              ) {
                duration = nextDetection.time - detection.time;
              }

              const shapeProps = {
                time: detection.time,
                duration: duration,
                x: `${box.x * 100}%`,
                y: `${box.y * 100}%`,
                width: `${box.width * 100}%`,
                height: `${box.height * 100}%`,
                z_index: 1,
                x_anchor: '0%',
                y_anchor: '0%',
                path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
                stroke_color: strokeColor,
                stroke_width: '0.5 vmin',
                fill_color: 'rgba(0,0,0,0)',
              };

              this.editor.timeline.addElement(
                trackId,
                Timeline.createElement('shape', shapeProps),
              );

              detectionIndex++;
            }
          }
        }

        rareDetections.sort((a, b) => a.time - b.time);
        let trackId = 15; // most certainly non occupied by video + top 10 face tracks. for testing purposes should be enough
        let lastDetectionTime = -1;
        for (const detection of rareDetections) {
          if (detection.time === lastDetectionTime) {
            trackId++;
          } else {
            trackId = 15;
          }
          if (trackId === 31) {
            continue; // cap faces tracked simultaneously up to max possible tracks (31)
          }
          lastDetectionTime = detection.time;

          const shapeProps = {
            time: Math.max(detection.time - 0.25, 0),
            duration: 0.5,
            x: `${detection.box[0] * 100}%`,
            y: `${detection.box[1] * 100}%`,
            width: `${detection.box[2] * 100}%`,
            height: `${detection.box[3] * 100}%`,
            z_index: 1,
            x_anchor: '0%',
            y_anchor: '0%',
            path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
            stroke_color: '#145c00',
            stroke_width: '0.5 vmin',
            fill_color: 'rgba(0,0,0,0)',
          };

          if (!this.editor.timeline.getTrack(String(trackId))) {
            this.editor.timeline.createTrack('shape', String(trackId));
          }

          this.editor.timeline.addElement(
            String(trackId),
            Timeline.createElement('shape', shapeProps),
          );
        }

        this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
          track:
            this.editor.timeline.tracks[this.editor.timeline.tracks.length - 1],
        });
        return true;
      },
      undo: () => false,
      getDescription: () => 'Add Face Detections',
      shouldCreateUndoRedoEntry: () => true,
    };

    this.editor.executeCommand(command);

    logger.info(
      `Added ${detections.length} face detections across ${detectionsByLabel.size} tracks (by label)`,
    );
  }
}
